// One print card for every label, rev 5. COHESION_CHANGE_LOG.md list 2, approved by Robert 2026-10-25
// (7:20 AM words, 7:38 AM mockup MOCKUP_ONE_PRINT_REV5.html: "wow! looks like i can now follow it. approved").
//
// Built on rev 4 (the Field Labels Print Preview). What rev 5 changed:
//   * Drive keeps one PDF per roll, always current, sorted by room with a room page first (master-pdf.ts).
//   * Update PDF rebuilds it. Then the card lists the pages to print, with Copy Pages for the desktop dialog.
//   * Tap a label to pick it. New and changed come picked. Tap any other to add a reprint. Tap a room page
//     for the whole room. Make PDF builds a small file of the picks for the iPad. It never goes to Drive.
//   * One Find box narrows the labels to an ID across every room. Add A Reprint is gone.
//   * Printer Offset left the card for Printer Setup (Program Settings). Off start, one amber line here.
//
// Pure render plus the shared clicks. Each page owns its items and its builds; the state shape is shared.

import "./asset-id.css";
import "./print-card.css";
import { esc } from "./training-screens";
import { sendStampMs } from "./driver-page";
import { masterPages, pagesForDialog, pagesWords } from "./master-pdf";
import { axisWord, nudge, offsetWords, readOffset, resetOffset, ROLL_WORDS, saveOffset, START_OFFSET, testLabelPdf, typedMm, type PrintRoll, type PrinterOffset } from "./printer-offset";

export type PrintTag = "new" | "changed" | "reprint";

/** One label as the card and the master see it. */
export interface PcItem {
  /** What a tap sends back: the Asset ID, the tag number, the label key. Unique on the card. */
  key: string;
  /** "ER1". Blank: no room (QEW tags, gear outside). */
  room: string;
  /** Against the master in Drive: not in it yet, in it but different now, or in it as it is. */
  state: "new" | "changed" | "current";
  /** Under a new or changed one: "Added Oct 23", "Was MCC 1". */
  why: string;
  /** Under a current one picked to reprint: "Last printed Oct 20", "Claimed · Dana Hill". */
  reprintWhy?: string;
  /** The label drawn small. */
  art: string;
  /** A problem to fix first: a Fix button under it. */
  fix?: boolean;
  /** Can't print at all (arc flash row with no values). Not in the master, never picked. */
  skip?: boolean;
  /** What Find looks in, beyond the key. */
  hay?: string;
}

/** One roll: one master PDF in Drive, one small print file on the iPad. */
export interface PcRoll {
  key: string;
  /** "2.25 × 1.0 in", "WARNING", "1 × 2 in". */
  title: string;
  /** "set the driver to 1 × 2 in", "load the WARNING roll". */
  sub: string;
  /** The master's Drive name: 10_Field_Labels_1x2.pdf. */
  driveName: string;
  /** "00 Field Labels". */
  folder: string;
  /** In master order: room by room, hanging order inside. */
  items: PcItem[];
  /** A room page before each room. */
  withRooms: boolean;
  /** QEW tags: made once, never updated from here. */
  once?: boolean;
  /**
   * The print changed (page size, QR_ROLL_1X3_WORDS.md rev 2; start position, 2026-10-30 11:23 AM). A Drive PDF sent
   * before `sinceMs` reads behind, so the card shows Update PDF. `note` says what changed, one sentence.
   */
  pageChange?: { sinceMs: number; note: string };
  /** The Drive PDF holds these labels in another order now (Field Labels). Shows the PDF behind. */
  moved?: boolean;
  rollColor?: string;
  /** Under the head, the page's own (field labels: Print At chips). */
  extra?: string;
  /** Printing at a size other than its default: the block turns amber. */
  offDefault?: boolean;
  /** The room page's line: "8 labels", "8 labels · WARNING". */
  roomLine?: (room: string, n: number) => string;
}

/** The latest Update PDF that reached the server, from driveFiles (master-send.ts). */
export interface MasterRecord {
  driveName: string;
  state: "saved" | "failed" | "retrying" | "waiting";
  savedAt: string;
  url: string;
  folderPath: string;
  folderUrl: string;
  reason: string;
  sendName: string;
}

/** The card's state, the same on every page. */
export interface PcState {
  picks: Set<string>;
  /** Rolls whose new and changed were picked for you already. */
  seeded: Set<string>;
  room: string;
  q: string;
  /** The small print file per roll, kept so Share works after. */
  small: Map<string, { file: File; count: number }>;
  /** The master built this visit per roll, for Download when Drive isn't connected or for a look. */
  masterFiles: Map<string, File>;
  /** The last record per Drive name, sends made this visit included. Null while it loads. */
  masters: Map<string, MasterRecord> | null;
  driveOn: boolean;
  /** "update:KEY", "make:KEY", "update:all" or "". */
  busy: string;
  error: string;
  copied: string;
  /** Blocks whose Reprint a few labels is open. Closed every time the card opens (ONE_UPDATE_BUTTON_WORDS.md). */
  open: Set<string>;
}

export function newPcState(room = ""): PcState {
  return { picks: new Set(), seeded: new Set(), room, q: "", small: new Map(), masterFiles: new Map(), masters: null, driveOn: false, busy: "", error: "", copied: "", open: new Set() };
}

/** New and changed come picked, once per roll, so a tap that takes one out sticks. */
export function seedPicks(s: PcState, rolls: readonly PcRoll[]): void {
  for (const r of rolls) {
    if (s.seeded.has(r.key)) continue;
    s.seeded.add(r.key);
    for (const i of r.items) if (!i.skip && i.state !== "current") s.picks.add(i.key);
  }
}

const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? "" : "s"}`;

/** "Oct 20". Blank when the date can't be read. */
export function shortDate(iso: unknown): string {
  const t = Date.parse(String(iso ?? ""));
  return Number.isFinite(t) ? new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";
}

// ---- What a roll is doing ----

export interface RollStatus {
  /** In the master, in order, each with its page number. Room pages included. */
  pages: ReturnType<typeof masterPages<PcItem>>;
  /** New or changed labels not in the Drive file yet. */
  waiting: number;
  /** The Drive file is missing or behind. Page numbers wait for Update PDF. */
  behind: boolean;
  record: MasterRecord | null;
  picked: PcItem[];
  pickedPages: number[];
}

/** The Drive PDF was sent before the roll's page size changed. A send that hasn't reached the server is new. */
export function oldPage(r: PcRoll, rec: MasterRecord | null): boolean {
  if (!r.pageChange || !rec?.sendName) return false;
  const at = sendStampMs(rec.sendName);
  return at > 0 && at < r.pageChange.sinceMs;
}

export function rollStatus(r: PcRoll, s: PcState): RollStatus {
  const inMaster = r.items.filter((i) => !i.skip);
  const pages = masterPages(inMaster, r.withRooms);
  const waiting = inMaster.filter((i) => i.state !== "current").length;
  const record = s.masters?.get(r.driveName) ?? null;
  // Missing counts only when Drive is on and the records have loaded: offline, the card can't know.
  const missing = !r.once && s.driveOn && s.masters !== null && !record && inMaster.length > 0;
  const behind = !r.once && (waiting > 0 || missing || Boolean(r.moved) || oldPage(r, record));
  const picked = inMaster.filter((i) => s.picks.has(i.key));
  const pickedPages = pages.filter((p) => p.item && s.picks.has(p.item.key)).map((p) => p.n);
  return { pages, waiting, behind, record, picked, pickedPages };
}

// ---- Render ----

export interface PcCard {
  title: string;
  /** What one item is called: "label" (the default) or "tag". */
  unit?: string;
  roll: PrintRoll;
  rolls: PcRoll[];
  state: PcState;
  /** Overrides the gold note. */
  note?: string;
  /** The page's own lines above the blocks: problem notes. */
  top?: string;
  /** Tiles shown before "N more" when no room or Find narrows them. */
  cap?: number;
  /** A small mark before a room's name on its button, like the walk's room light. */
  roomLead?: (room: string) => string;
  /**
   * The short top (Field Labels, field test 2026-10-28 4:20 PM): one count line with Update All on it, no Find,
   * no gold note. The card is for looking and updating.
   */
  slim?: boolean;
  /** Room buttons in this order (the walk's). Rooms not in it follow, as they come. */
  roomOrder?: readonly string[];
  /** The page's own line at the bottom of the card. */
  bottom?: string;
}

const TAG: Record<PrintTag, string> = { new: "NEW", changed: "CHANGED", reprint: "REPRINT" };

/** The amber line when this iPad prints off start for the roll. Empty at start. */
export function offsetLine(roll: PrintRoll, o: PrinterOffset = readOffset(roll)): string {
  const st = START_OFFSET[roll];
  if (o.acrossMm === st.acrossMm && o.downMm === st.downMm) return "";
  return `<div class="pc-offline"><span><b>Printer offset is on:</b> ${esc(offsetWords(o))}. Change it in Printer Setup.</span><button class="ac-tbtn" type="button" data-pc="setup">Printer Setup</button></div>`;
}

function itemTile(i: PcItem, n: number, picked: boolean, behind: boolean): string {
  const tag: PrintTag | null = i.state === "new" ? "new" : i.state === "changed" ? "changed" : picked ? "reprint" : null;
  const why = i.skip ? i.why : i.state !== "current" ? i.why : picked ? (i.reprintWhy ?? "Picked to reprint") : "";
  const num = i.skip ? "Not in PDF" : behind && i.state !== "current" ? "Goes in" : `p ${n}`;
  const fix = i.fix ? `<button type="button" class="ac-tbtn pc-fix" data-pc="fix" data-key="${esc(i.key)}">Fix</button>` : "";
  const label = `${i.key}${tag ? `, ${TAG[tag]}` : ""}${picked ? ", picked" : ""}`;
  return `<div class="pc-lb${picked ? " on" : ""}${i.fix || i.skip ? " bad" : ""}${i.skip ? " skip" : ""}" role="button" tabindex="0" data-pc="label" data-key="${esc(i.key)}" aria-pressed="${picked}" aria-label="${esc(label)}"><span class="pc-num"><span>${esc(num)}</span>${tag ? `<span class="pc-tag ${tag}">${TAG[tag]}</span>` : ""}</span>${i.art}<span class="pc-why${i.state === "changed" ? " pc-was" : ""}">${esc(why)}</span>${fix}</div>`;
}

function roomTile(r: PcRoll, block: string, n: number, room: string, count: number, allOn: boolean, art: string): string {
  return `<div class="pc-lb pc-roompg${allOn ? " on" : ""}" role="button" tabindex="0" data-pc="roompage" data-block="${esc(block)}" data-room="${esc(room)}" aria-pressed="${allOn}" aria-label="${esc(`Room page ${room}, ${allOn ? "whole room picked" : "tap to pick the whole room"}`)}"><span class="pc-num"><span>p ${n}</span><span class="pc-tag room">ROOM</span></span>${art}<span class="pc-why">${allOn ? "Whole room picked" : "Room page · tap for all"}</span></div>`;
}

/** The room page drawn small, the size of the labels next to it. */
function roomArt(room: string, line: string, like: string): string {
  // Borrow the size from the first label's drawing: its class and inline size, then dash it.
  const cls = /class="([^"]*)"/.exec(like)?.[1] ?? "pc-art qr";
  const style = /style="([^"]*)"/.exec(like)?.[1] ?? "";
  return `<span class="${esc(cls)} pc-roomart"${style ? ` style="${style.replace(/font-size:[^;"]*;?/, "")}"` : ""}><b>${esc(room)}</b><small>${esc(line)}</small></span>`;
}

function masterLine(r: PcRoll, st: RollStatus, s: PcState): string {
  const rec = st.record;
  const link = rec?.url ? `<a class="pc-link" href="${esc(rec.url)}" target="_blank" rel="noopener">Open In Drive</a>` : rec?.folderUrl ? `<a class="pc-link" href="${esc(rec.folderUrl)}" target="_blank" rel="noopener">Open In Drive</a>` : "";
  const here = s.masterFiles.has(r.key) ? `<button class="ac-tbtn" type="button" data-pc="mdownload" data-block="${esc(r.key)}">Download PDF</button>` : "";
  if (r.once) {
    return `<div class="pc-master ok"><span><b>Made once.</b> ${esc(r.folder)} holds the site's tag PDF. QEW tags never change.</span><span class="pc-sp">${link}</span></div>`;
  }
  const busy = s.busy === `update:${r.key}` || s.busy === "update:all";
  if (busy) return `<div class="pc-master stale"><span><b>Updating the PDF…</b> Every label, room by room.</span></div>`;
  const inMaster = st.pages.filter((p) => p.item).length;
  if (st.behind) {
    const what = !s.driveOn
      ? `<b>Drive is not connected.</b> ${st.waiting ? `${plural(st.waiting, "label")} ${st.waiting === 1 ? "is" : "are"} new or changed. ` : ""}Update PDFs builds the file here to download.`
      : !st.record
        ? `<b>No PDF in Drive yet.</b> Update PDFs builds it with all ${plural(inMaster, "label")}.`
        : `<b>Drive PDF is behind.</b> ${st.waiting ? `${plural(st.waiting, "label")} ${st.waiting === 1 ? "waits" : "wait"} to go in.` : ""}${r.moved ? `${st.waiting ? " " : ""}The order changed.` : ""}${oldPage(r, rec) ? `${st.waiting || r.moved ? " " : ""}${esc(r.pageChange!.note)}` : ""}${rec?.savedAt ? ` Last updated ${esc(shortDate(rec.savedAt))}.` : ""}`;
    // One Update PDFs at the top of the card does every behind file (ONE_UPDATE_BUTTON_WORDS.md, 2026-10-30).
    return `<div class="pc-master stale"><span>${what}</span><span class="pc-sp">${here}</span></div>`;
  }
  if (rec && (rec.state === "failed")) {
    return `<div class="pc-master bad"><span><b>Not saved to Drive.</b> ${esc(rec.reason)}</span><span class="pc-sp">${here}<button class="ac-tbtn" type="button" data-pc="retry" data-block="${esc(r.key)}">Try Again</button></span></div>`;
  }
  if (rec && rec.state !== "saved") {
    return `<div class="pc-master stale"><span><b>Waiting for Drive.</b> The PDF saves on signal, same name, same link.</span><span class="pc-sp">${here}</span></div>`;
  }
  const when = rec?.savedAt ? ` · updated ${esc(shortDate(rec.savedAt))}` : "";
  const where = rec?.folderPath ? ` in ${esc(rec.folderPath)}` : s.driveOn ? "" : " · Drive is not connected";
  return `<div class="pc-master ok"><span><b>${s.driveOn ? "Drive PDF is current." : "The PDF is current."}</b> ${plural(st.pages.length, "page")}${r.withRooms ? ", room pages included" : ""}${when}${where}.</span><span class="pc-sp">${here}${link}</span></div>`;
}

function rollBlock(r: PcRoll, s: PcState, q: string, cap: number, unit: string, find = true): string {
  const st = rollStatus(r, s);
  const narrowed = Boolean(s.room || q);
  const hit = (i: PcItem) => !q || `${i.key} ${i.hay ?? ""}`.toUpperCase().includes(q);
  const skipped = r.items.filter((i) => i.skip && (!s.room || i.room === s.room) && hit(i));
  const shown = st.pages.filter((p) => (p.item ? (!s.room || p.item.room === s.room) && hit(p.item) : !q && (!s.room || p.room!.name === s.room)));
  if (!shown.some((p) => p.item) && !skipped.length) return "";
  const firstArt = r.items[0]?.art ?? "";
  const tiles: string[] = [];
  let more = 0;
  for (const p of shown) {
    if (!narrowed && tiles.length >= cap) {
      if (p.item) more++;
      continue;
    }
    if (p.room) {
      const ids = r.items.filter((i) => !i.skip && i.room === p.room!.name).map((i) => i.key);
      const line = r.roomLine ? r.roomLine(p.room.name, p.room.count) : plural(p.room.count, unit);
      tiles.push(roomTile(r, r.key, p.n, p.room.name, p.room.count, ids.length > 0 && ids.every((k) => s.picks.has(k)), roomArt(p.room.name, line, firstArt)));
    } else tiles.push(itemTile(p.item!, p.n, s.picks.has(p.item!.key), st.behind));
  }
  for (const i of skipped) tiles.push(itemTile(i, 0, false, st.behind));
  const small = s.small.get(r.key);
  const making = s.busy === `make:${r.key}`;
  const btns = small
    ? `<button class="ac-tbtn dark" type="button" data-pc="share" data-block="${esc(r.key)}">Share PDF</button><button class="ac-tbtn" type="button" data-pc="download" data-block="${esc(r.key)}">Download</button>`
    : `<button class="ac-tbtn dark" type="button" data-pc="make" data-block="${esc(r.key)}"${st.picked.length && !s.busy ? "" : " disabled"}>${making ? "Making…" : `Make PDF · ${st.picked.length}`}</button>`;
  const copy = s.copied === r.key ? "Copied" : "Copy Pages";
  const pl = !st.picked.length
    ? `<span class="pc-pl dim">Tap a ${esc(unit)} to pick it.</span>`
    : st.behind
      ? `<span class="pc-pl">${st.picked.length} picked · page numbers come with Update PDFs</span>`
      : `<span class="pc-pl">${st.picked.length} picked · <b>Pages ${esc(pagesWords(st.pickedPages))}</b></span><button class="ac-tbtn" type="button" data-pc="copy" data-block="${esc(r.key)}" data-pages="${esc(pagesForDialog(st.pickedPages))}">${copy}</button>`;
  const madeLine = small ? `<span class="pc-pl">Print file made: ${plural(small.count, unit)}${r.withRooms ? ", no room pages" : ""}. For the printer only. It does not go to Drive.</span>` : "";
  // The reprint tools fold away under one line (ONE_UPDATE_BUTTON_WORDS.md). Open when tapped, when Find or a room
  // narrows the card, or on a made-once roll (QEW tags), whose card is only for reprints.
  const open = Boolean(r.once || q || s.open.has(r.key));
  const tools = open
    ? `${r.extra ?? ""}
    <div class="pc-labs">${tiles.join("")}${more ? `<span class="pc-more">${more} more. Pick a room${find ? " or use Find" : ""}.</span>` : ""}</div>
    <div class="pc-pick">${small ? madeLine : pl}<span class="pc-btns">${btns}</span></div>`
    : "";
  const fold = r.once ? "" : `<button class="pc-fold${open ? " on" : ""}" type="button" data-pc="fold" data-block="${esc(r.key)}" aria-expanded="${open}">${open ? "Hide reprint" : "Reprint a few labels"}</button>`;
  return `<div class="pc-block${r.offDefault ? " off" : ""}">
    <div class="pc-hd">${r.rollColor ? `<span class="pc-roll" style="background:${esc(r.rollColor)}"></span>` : ""}<b>${esc(r.title)}</b> · ${esc(r.sub)}</div>
    <div class="pc-file">${esc(r.driveName)}</div>
    ${masterLine(r, st, s)}
    ${fold}
    ${tools}
  </div>`;
}

/** The gold note: what the card is showing, and the one thing to know. */
export function noteFor(rolls: readonly PcRoll[], s: PcState, unit: string): string {
  if (!rolls.some((r) => r.items.length)) return `<b>No ${esc(unit)}s yet.</b> They show here as they're added.`;
  if (rolls.length && rolls.every((r) => r.once)) return `<b>${unit === "tag" ? "QEW tags never change." : "Made once."}</b> Lost one? Type its number in Find, tap it, then Make PDF.`;
  if (rolls.some((r) => rollStatus(r, s).behind)) return `<b>Update PDFs</b> at the top puts every change in the Drive files.`;
  return `<b>The PDFs are current.</b> Print from Drive. Reprint a few labels is under each PDF.`;
}

// ---- The card keeps its place ----
// Every tap redraws the card, and a new card opens at the top. Robert, 2026-10-25: "when i tap the screen
// want to go to the top." The card's scroll is kept here and put back on each redraw of the same card,
// whatever page drew it. Closing the card forgets it, so the next open starts at the top.

let kept = { key: "", top: 0 };
let watching = false;

function keepCardScroll(): void {
  if (watching || typeof document === "undefined" || typeof MutationObserver === "undefined") return;
  watching = true;
  document.addEventListener(
    "scroll",
    (e) => {
      const t = e.target;
      if (t instanceof HTMLElement && t.classList.contains("pc-card")) kept = { key: t.dataset.pcKey ?? "", top: t.scrollTop };
    },
    true,
  );
  // Runs after the redraw and before the screen paints, so the card never shows at the top first.
  new MutationObserver(() => {
    const c = document.querySelector<HTMLElement>(".pc-card");
    if (!c) {
      kept = { key: "", top: 0 };
      return;
    }
    if (c.dataset.pcKey !== kept.key) {
      kept = { key: c.dataset.pcKey ?? "", top: 0 };
      return;
    }
    if (Math.abs(c.scrollTop - kept.top) > 1) c.scrollTop = kept.top;
  }).observe(document.body, { childList: true, subtree: true });
}

/**
 * The one Update PDFs, top of the card, always in the same place (ONE_UPDATE_BUTTON_WORDS.md, 2026-10-30).
 * Gold with the count when something is behind; grey when every PDF is current. None on a made-once card.
 */
export function updateBar(c: PcCard, behindKeys: number): string {
  if (c.rolls.every((r) => r.once)) return "";
  const s = c.state;
  if (s.busy === "update:all") return `<button class="pc-update" type="button" disabled>Updating…</button>`;
  if (!behindKeys) return `<button class="pc-update done" type="button" disabled>All PDFs current</button>`;
  return `<button class="pc-update" type="button" data-pc="updateall"${s.busy ? " disabled" : ""}>Update PDFs · ${behindKeys}</button>`;
}

/** The card, inside the app's sheet. */
export function printCard(c: PcCard): string {
  keepCardScroll();
  const s = c.state;
  const unit = c.unit ?? "label";
  const q = s.q.trim().toUpperCase();
  const items = c.rolls.flatMap((r) => r.items);
  const statuses = c.rolls.map((r) => rollStatus(r, s));
  const fresh = items.filter((i) => !i.skip && i.state === "new").length;
  const changed = items.filter((i) => !i.skip && i.state === "changed").length;
  const picked = statuses.reduce((a, x) => a + x.picked.length, 0);
  const anyBehind = statuses.some((x) => x.behind);
  const count = `<div class="pc-count"><span><b>${items.filter((i) => !i.skip).length}</b> ${esc(unit)}s in ${c.rolls.length > 1 ? `${c.rolls.length} PDFs` : "the PDF"}</span>${anyBehind && fresh ? `<span>${fresh} new</span>` : ""}${anyBehind && changed ? `<span class="pc-chg">${changed} changed</span>` : ""}<span>${picked} picked</span></div>`;
  const rooms: { name: string; count: number }[] = [];
  for (const i of items) {
    if (!i.room) continue;
    const r = rooms.find((x) => x.name === i.room);
    if (r) r.count++;
    else rooms.push({ name: i.room, count: 1 });
  }
  if (c.roomOrder) {
    const at = (n: string) => {
      const i = c.roomOrder!.indexOf(n);
      return i < 0 ? c.roomOrder!.length : i;
    };
    rooms.sort((a, b) => at(a.name) - at(b.name));
  }
  const roomBtns =
    rooms.length > 1
      ? `<div class="pc-rooms">${[{ name: "", count: items.length }, ...rooms]
          .map((r) => `<button type="button" class="pc-roombtn${s.room === r.name ? " cur" : ""}" data-pc="room" data-room="${esc(r.name)}">${r.name && c.roomLead ? c.roomLead(r.name) : ""}${r.name ? esc(r.name) : "All Rooms"}<small>${r.count}</small></button>`)
          .join("")}</div>`
      : "";
  const behindKeys = c.rolls.filter((r, k) => statuses[k]!.behind).length;
  if (c.slim) return slimCard(c, items, changed, behindKeys, roomBtns, unit, q);
  const updateAll = updateBar(c, behindKeys);
  const blocks = c.rolls.map((r) => rollBlock(r, s, q, c.cap ?? 60, unit)).join("");
  return `<div class="ai-sheet pc-sheet" data-pc-sheet><div class="ai-card pc-card" data-pc-key="${esc(c.title)}">
    <div class="ai-cardhd"><b>${esc(c.title)}</b><button class="ai-tbtn" type="button" data-pc="close">Close</button></div>
    ${offsetLine(c.roll)}
    ${count}
    <div class="pc-tools">${roomBtns}<input type="search" class="pc-q" id="pc-q" data-pc-q autocomplete="off" autocapitalize="characters" placeholder="Find: type part of the ${esc(unit === "tag" ? "tag number" : "ID")}" value="${esc(s.q)}" aria-label="Find a ${esc(unit)}"></div>
    <div class="ai-annot">${c.note ?? noteFor(c.rolls, s, unit)}</div>
    ${c.top ?? ""}
    ${updateAll}
    ${blocks || (s.q.trim() ? `<div class="ai-annot">No ${esc(unit)} matches "${esc(s.q)}".</div>` : "")}
    ${s.error ? `<div class="note red">${esc(s.error)}</div>` : ""}
  </div></div>`;
}

function slimCard(c: PcCard, items: readonly PcItem[], changed: number, behindKeys: number, roomBtns: string, unit: string, q: string): string {
  const s = c.state;
  const pdfs = c.rolls.length === 1 ? "1 PDF" : `${c.rolls.length} PDFs`;
  const count = `<div class="pc-count slim"><span><b>${items.filter((i) => !i.skip).length}</b> ${esc(unit)}s · ${pdfs}</span>${changed ? `<span class="pc-chg">${changed} changed</span>` : ""}</div>${updateBar(c, behindKeys)}`;
  const blocks = c.rolls.map((r) => rollBlock(r, s, q, c.cap ?? 60, unit, false)).join("");
  return `<div class="ai-sheet pc-sheet" data-pc-sheet><div class="ai-card pc-card" data-pc-key="${esc(c.title)}">
    <div class="ai-cardhd"><b>${esc(c.title)}</b><button class="ai-tbtn" type="button" data-pc="close">Close</button></div>
    ${offsetLine(c.roll)}
    ${count}
    ${roomBtns ? `<div class="pc-tools">${roomBtns}</div>` : ""}
    ${c.top ?? ""}
    ${blocks}
    ${s.error ? `<div class="note red">${esc(s.error)}</div>` : ""}
    ${c.bottom ?? ""}
  </div></div>`;
}

// ---- Shared clicks ----

export type PcAct =
  | { act: "close" | "setup" | "updateall" }
  | { act: "update" | "make" | "share" | "download" | "mdownload" | "retry"; block: string }
  | { act: "fix"; key: string }
  | { act: "repaint" };

/**
 * A data-pc click. Picks, rooms and Copy Pages are handled here and come back as repaint. The rest come back
 * for the page to do. Null when the click wasn't the card's.
 */
export function cardClick(el: HTMLElement, s: PcState, rolls: readonly PcRoll[]): PcAct | null {
  const block = el.dataset.block ?? "";
  switch (el.dataset.pc) {
    case "close":
    case "setup":
    case "updateall":
      return { act: el.dataset.pc };
    case "room":
      s.room = el.dataset.room ?? "";
      return { act: "repaint" };
    case "fold":
      if (s.open.has(block)) s.open.delete(block);
      else s.open.add(block);
      return { act: "repaint" };
    case "label": {
      const key = el.dataset.key ?? "";
      const roll = rolls.find((r) => r.items.some((i) => i.key === key));
      const it = roll?.items.find((i) => i.key === key);
      if (!roll || !it || it.skip) return { act: "repaint" };
      if (s.picks.has(key)) s.picks.delete(key);
      else s.picks.add(key);
      s.small.delete(roll.key);
      return { act: "repaint" };
    }
    case "roompage": {
      const roll = rolls.find((r) => r.key === block);
      if (!roll) return { act: "repaint" };
      const ids = roll.items.filter((i) => !i.skip && i.room === el.dataset.room).map((i) => i.key);
      const all = ids.every((k) => s.picks.has(k));
      for (const k of ids) all ? s.picks.delete(k) : s.picks.add(k);
      s.small.delete(roll.key);
      return { act: "repaint" };
    }
    case "copy": {
      const text = el.dataset.pages ?? "";
      try {
        void navigator.clipboard?.writeText(text).catch(() => undefined);
      } catch {
        /* the button still says Copied; the pages are on the card to type */
      }
      s.copied = block;
      setTimeout(() => {
        if (s.copied === block) s.copied = "";
      }, 2500);
      return { act: "repaint" };
    }
    case "fix":
      return { act: "fix", key: el.dataset.key ?? "" };
    case "update":
    case "make":
    case "share":
    case "download":
    case "mdownload":
    case "retry":
      return { act: el.dataset.pc, block };
    default:
      return null;
  }
}

/** Typing in Find. Keeps the cursor where it was after the repaint. */
export function cardFind(t: HTMLInputElement, s: PcState, repaint: () => void, root: ParentNode): boolean {
  if (t.dataset.pcQ === undefined) return false;
  s.q = t.value;
  const at = t.selectionStart ?? t.value.length;
  repaint();
  const box = root.querySelector<HTMLInputElement>("#pc-q");
  if (box) {
    box.focus();
    box.setSelectionRange(at, at);
  }
  return true;
}

/** The picked items of a roll, in master order. */
export function pickedOf(r: PcRoll, s: PcState): PcItem[] {
  return r.items.filter((i) => !i.skip && s.picks.has(i.key));
}

/** The room sections of a roll, in master order: what the master is built from. */
export function sectionsOf(r: PcRoll): { room: string; items: PcItem[] }[] {
  const out: { room: string; items: PcItem[] }[] = [];
  for (const i of r.items) {
    if (i.skip) continue;
    const last = out[out.length - 1];
    if (last && last.room === i.room) last.items.push(i);
    else {
      const had = out.find((x) => x.room === i.room);
      if (had) had.items.push(i);
      else out.push({ room: i.room, items: [i] });
    }
  }
  return out;
}

// ---- Offset and test label: Printer Setup uses these ----

/** A data-pc click about the offset. Returns the new offset, or null when the click was something else. */
export function handleOffsetClick(el: HTMLElement, roll: PrintRoll): PrinterOffset | null {
  const act = el.dataset.pc;
  if (act === "nudge") {
    const axis = el.dataset.axis === "across" ? "across" : "down";
    return saveOffset(roll, nudge(readOffset(roll), axis, el.dataset.dir === "-1" ? -1 : 1));
  }
  if (act === "reset") return resetOffset(roll);
  return null;
}

/**
 * A typed value in an offset box. The box shows the size, and the word beside it the direction, so a typed
 * number keeps the direction already set (a sign is ignored). Left, Right, Up and Down change direction.
 * At 0 a typed number goes right or down. Returns the new offset, or null when it wasn't a number.
 */
export function handleOffsetInput(el: HTMLInputElement, roll: PrintRoll): PrinterOffset | null {
  const mm = typedMm(el.value);
  if (mm === null) return null;
  const o = readOffset(roll);
  const across = el.dataset.pcMm === "across";
  const now = across ? o.acrossMm : o.downMm;
  const v = (now < 0 ? -1 : 1) * Math.abs(mm);
  return saveOffset(roll, across ? { ...o, acrossMm: v } : { ...o, downMm: v });
}

/** The offset box for one roll: Left, Right, Up, Down, the typed values, the stock drawing. */
export function offsetBox(roll: PrintRoll, o: PrinterOffset): string {
  const axis = (name: string, key: "across" | "down", lo: string, hi: string) => {
    const mm = key === "across" ? o.acrossMm : o.downMm;
    return `<div class="pc-axis"><span class="pc-nm">${name}</span><button class="ac-tbtn" type="button" data-pc="nudge" data-roll="${roll}" data-axis="${key}" data-dir="-1">${lo}</button><span class="pc-val"><input id="pc-mm-${roll}-${key}" inputmode="decimal" data-pc-mm="${key}" data-roll="${roll}" value="${Math.abs(mm).toFixed(2)}" aria-label="${name} in millimeters, ${axisWord(key, mm) || "none"}"><span>mm ${axisWord(key, mm)}</span></span><button class="ac-tbtn" type="button" data-pc="nudge" data-roll="${roll}" data-axis="${key}" data-dir="1">${hi}</button></div>`;
  };
  const st = START_OFFSET[roll];
  const atStart = o.acrossMm === st.acrossMm && o.downMm === st.downMm;
  return `<div class="pc-offbox"><div class="pc-axes">${axis("Across", "across", "Left", "Right")}${axis("Down", "down", "Up", "Down")}<div class="pc-minor"><button class="ac-tbtn dark" type="button" data-pc="test" data-roll="${roll}">Print Test Label</button><button class="ac-tbtn" type="button" data-pc="reset" data-roll="${roll}"${atStart ? " disabled" : ""}>Reset To Start</button></div></div><span class="pc-stock${roll === "arcFlashRoll" ? " af" : roll === "fieldRoll" ? " fl" : ""}" aria-hidden="true"><i style="transform:translate(${(o.acrossMm * 4).toFixed(1)}px,${(o.downMm * 4).toFixed(1)}px)"></i></span></div>`;
}

/** Shares a file the iPad way, or downloads it where sharing isn't offered. */
export async function shareFile(file: File): Promise<void> {
  const nav = navigator as Navigator & { canShare?: (d: { files: File[] }) => boolean };
  if (nav.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: file.name });
      return;
    } catch (e) {
      if ((e as { name?: string })?.name === "AbortError") return;
    }
  }
  downloadFile(file);
}

export function downloadFile(file: File): void {
  const url = URL.createObjectURL(file);
  // Chrome on the iPad will not take a download link from a web page. It opens the PDF in a tab instead,
  // where Chrome's own share and save work. Safari keeps the download.
  if (/CriOS|FxiOS|EdgiOS/.test(navigator.userAgent)) {
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 120_000);
    return;
  }
  const a = document.createElement("a");
  a.href = url;
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

/** One test page for the roll, at this iPad's offset, straight to the share sheet. Never counts as printed. */
export async function printTestLabel(roll: PrintRoll, widthIn: number, heightIn: number): Promise<void> {
  const o = readOffset(roll);
  const bytes = await testLabelPdf(widthIn, heightIn, o, ROLL_WORDS[roll]);
  await shareFile(new File([bytes as Uint8Array<ArrayBuffer>], `SafeTag_Test_Label_${widthIn}x${heightIn}.pdf`, { type: "application/pdf" }));
}
